import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { TrendingUp, Shield, Zap, Target, CheckCircle2, BarChart3, Brain, Lock, Users, ArrowRight, Sparkles, Activity, TrendingDown, Clock, LineChart, Filter, Eye, Star } from 'lucide-react';
import { strategies, testimonials, features, whyCopytrade, platformFeatures, differentiators } from '../mock';
import LiveCounter from '../components/LiveCounter';
import WhatsAppButton from '../components/WhatsAppButton';
import ROICalculator from '../components/ROICalculator';
import ComparisonTable from '../components/ComparisonTable';
import FAQ from '../components/FAQ';
import LimitedSlots from '../components/LimitedSlots';
import SocialProofStats from '../components/SocialProofStats';
import LiveActivityFeed from '../components/LiveActivityFeed';
import HowItWorks from '../components/HowItWorks';
import TrustSafety from '../components/TrustSafety';
import UrgencyBanner from '../components/UrgencyBanner';
import AIAnalyzeAnimation from '../components/AIAnalyzeAnimation';
import MoneyBackGuarantee from '../components/MoneyBackGuarantee';
import TraderStory from '../components/TraderStory';
import { trackLead } from '../utils/fbPixel';

const LandingPage = () => {
  const [hoveredStrategy, setHoveredStrategy] = useState(null);
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="sahara-header">
        <div className="header-content">
          <div className="logo-container" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer' }}>
            <img src="/images/sahara-logo.png" alt="Sahara AI Ecosystem" className="header-logo" />
          </div>
          <nav className="nav-links">
            <button onClick={() => scrollToSection('story')} className="nav-link">Cerita Kami</button>
            <button onClick={() => scrollToSection('platform')} className="nav-link">Platform</button>
            <button onClick={() => scrollToSection('strategies')} className="nav-link">Strategi</button>
            <button onClick={() => scrollToSection('proof')} className="nav-link">Bukti</button>
          </nav>
          <Button className="btn-primary-sahara" onClick={() => {
            trackLead();
            window.open('https://t.me/saharacopytrade', '_blank');
          }}>Mulai Copy Trading</Button>
        </div>
      </header>

      {/* Hero Section - Strong Hook */}
      <section className="hero-section-new">
        <div className="hero-background-pattern"></div>
        <div className="hero-content-new">
          <div className="hero-ecosystem-badge">PLATFORM COPY TRADING TEREGULASI</div>
          <h1 className="hero-title-new">
            Copy Strategi Trader Profesional
            <span className="hero-title-highlight"> Secara Otomatis</span>
          </h1>
          <p className="hero-subtitle-new">
            Sistem AI yang menganalisis dan memfilter strategi trading terbaik untuk kamu.
          </p>
          <p className="hero-description">
            Sahara AI menyediakan platform copy trading yang transparan dan aman. Sistem kami menganalisis ribuan strategi setiap hari menggunakan AI, memberikan kamu akses ke strategi yang sudah terbukti performanya dengan data real dan terverifikasi.
          </p>
          <div className="hero-cta-new">
            <Button className="btn-primary-sahara btn-large" onClick={() => {
              trackLead('Hero CTA - Mulai Copy Trading');
              window.open('https://t.me/saharacopytrade', '_blank');
            }} style={{ 
              fontSize: '17px',
              padding: '18px 36px'
            }}>
              Mulai Copy Trading
              <ArrowRight className="btn-icon" />
            </Button>
            <Button className="btn-secondary-sahara btn-large" onClick={() => scrollToSection('strategies')}>
              Lihat Strategi Tersedia
              <TrendingUp className="btn-icon" />
            </Button>
          </div>
          <p className="cta-trust-text">
            Gratis untuk memulai • Tanpa biaya setup • Partner dengan broker teregulasi
          </p>
          <p style={{
            fontSize: '13px',
            color: 'rgba(0, 0, 0, 0.5)',
            marginTop: '12px',
            fontStyle: 'italic'
          }}>
            *Trading melibatkan risiko. Performa masa lalu tidak menjamin hasil di masa depan.
          </p>
        </div>
        <div className="hero-chart-overlay">
          <Activity className="chart-icon" />
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />

      {/* AI Analyze Animation */}
      <AIAnalyzeAnimation />

      {/* Live Activity Feed */}
      <LiveActivityFeed />

      {/* Social Proof Stats Banner */}
      <SocialProofStats />

      {/* Trader Story - Emotional & Authentic */}
      <TraderStory />
      
      {/* Urgency Banner */}
      <UrgencyBanner />
      
      {/* Money Back Guarantee */}
      <section style={{ padding: '60px 7.6923%', background: 'var(--light-gray)' }}>
        <div className="section-container">
          <MoneyBackGuarantee />
        </div>
      </section>

      {/* Story Section - The Pain */}
      <section id="story" className="story-section">
        <div className="section-container">
          <div className="story-header">
            <h2 className="story-title">Cerita yang Semua Trader Pasti Ngerasain</h2>
          </div>
          <div className="story-narrative">
            <p className="story-paragraph">Trading manual membutuhkan waktu, pengetahuan mendalam, dan kontrol emosi yang kuat.</p>
            <p className="story-paragraph">Kebanyakan trader pemula mengalami kerugian karena kurangnya pengalaman dan strategi yang teruji.</p>
            <p className="story-paragraph">Copy trading memberikan solusi dengan memungkinkan kamu mengikuti strategi dari trader berpengalaman yang sudah terbukti performanya.</p>
            <div className="story-truth">
              <h3 className="story-truth-title">Fakta tentang Copy Trading:</h3>
              <p className="story-truth-text">Copy trading bukan jaminan profit, tapi memberikan akses ke <span className="emphasis">strategi yang sudah diuji dengan data historis nyata.</span></p>
            </div>
            <p className="story-paragraph">Platform kami menggunakan AI untuk menganalisis performa strategi secara real-time, memberikan transparansi penuh atas metrik seperti win rate, drawdown, dan return historis.</p>
            <div className="story-power">
              <p className="story-power-text">Dengan copy trading yang tepat dan risk management yang baik, kamu dapat mengakses peluang trading tanpa perlu menjadi expert sendiri. Tapi ingat: semua investasi memiliki risiko.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Copytrade Section */}
      <section className="why-copytrade-section">
        <div className="section-container">
          <h2 className="section-title">Kenapa Copytrade Mengubah Segalanya</h2>
          <div className="copytrade-grid">
            {whyCopytrade.map((item, index) => (
              <div key={index} className="copytrade-card">
                <div className="copytrade-icon">{item.icon}</div>
                <h3 className="copytrade-title">{item.title}</h3>
                <p className="copytrade-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sahara AI Section - Differentiation */}
      <section className="why-sahara-section">
        <div className="section-container">
          <div className="why-sahara-intro">
            <p className="intro-pretext">Tapi ini yang perlu kamu tahu —</p>
            <h2 className="section-title-alt">Gak Semua Platform Copytrade Itu Sama.</h2>
            <p className="intro-description">
              Ada yang ngaku "AI" tapi masih andalkan keberuntungan doang.<br />
              Ada yang biarkan sembarang orang jadi "master" tanpa performa jelas.
            </p>
            <div className="sahara-different">
              <h3 className="different-title">Sahara AI beda dari yang lain.</h3>
            </div>
          </div>
          <div className="differentiators-grid">
            {differentiators.map((item, index) => (
              <div key={index} className="diff-card">
                <div className="diff-icon">{item.icon}</div>
                <h3 className="diff-title">{item.title}</h3>
                <p className="diff-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features Section */}
      <section id="platform" className="platform-section">
        <div className="section-container">
          <h2 className="section-title">Dashboard Kamu. Simpel. Powerful. Milik Kamu.</h2>
          <p className="platform-intro">
            Semua yang kamu butuhin buat monitor, kontrol, dan profit — dalam satu interface yang bersih.
          </p>
          <div className="platform-features-grid">
            {platformFeatures.map((feature, index) => (
              <div key={index} className="platform-feature-card">
                <div className="platform-feature-icon">{feature.icon}</div>
                <h3 className="platform-feature-title">{feature.title}</h3>
                <p className="platform-feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
          <div className="platform-message">
            <p className="platform-message-text">
              Karena trading gak harus terasa kayak perang.<br />
              <span className="emphasis">Harusnya terasa seperti kebebasan.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Strategy Showcase - Proof Section */}
      <section id="strategies" className="strategy-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Strategi Terbukti. Hasil Nyata.</h2>
            <p className="section-description">
              Cuma yang terbaik yang lolos sistem filter AI kami. Return tinggi. Risiko rendah. Performa terverifikasi.
            </p>
            <div className="strategy-cta-banner">
              <BarChart3 className="banner-icon" />
              <p className="banner-text">
                Klik <span className="highlight-text">"Lihat Detail"</span> untuk melihat grafik performa lengkap, alokasi aset, dan statistik trading untuk setiap strategi.
              </p>
            </div>
          </div>
          <div className="strategy-table">
            <div className="table-header">
              <div className="table-cell">Strategi</div>
              <div className="table-cell">Gaya</div>
              <div className="table-cell">Win Rate</div>
              <div className="table-cell">Level Risiko</div>
              <div className="table-cell">Return (3B)</div>
              <div className="table-cell">Copiers</div>
              <div className="table-cell">Status</div>
              <div className="table-cell">Aksi</div>
            </div>
            {strategies.map((strategy, index) => (
              <div 
                key={index} 
                className={`table-row ${hoveredStrategy === index ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredStrategy(index)}
                onMouseLeave={() => setHoveredStrategy(null)}
              >
                <div className="table-cell strategy-name-cell">
                  <Brain className="strategy-icon" />
                  <div>
                    <div className="strategy-name">{strategy.name}</div>
                    <div className="strategy-id">{strategy.strategyId}</div>
                  </div>
                </div>
                <div className="table-cell">{strategy.style}</div>
                <div className="table-cell win-rate">{strategy.winRate}</div>
                <div className="table-cell risk-level">{strategy.drawdown}</div>
                <div className="table-cell roi">{strategy.roi}</div>
                <div className="table-cell copiers-count">{strategy.copiers}</div>
                <div className="table-cell status">
                  <CheckCircle2 className="status-icon" />
                  {strategy.status}
                </div>
                <div className="table-cell action-cell">
                  <Button 
                    className="btn-view-details"
                    onClick={() => {
                      trackLead(`View Strategy - ${strategy.name}`);
                      navigate(`/strategy/${strategy.strategyId}`);
                    }}
                  >
                    Lihat Detail
                    <ArrowRight className="btn-icon-small" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <Button className="btn-primary-sahara btn-large" onClick={() => {
              trackLead('Strategy Section CTA');
              window.open('https://t.me/saharacopytrade', '_blank');
            }} style={{
              fontSize: '17px',
              padding: '18px 40px'
            }}>
              Mulai Copy Trading
              <ArrowRight className="btn-icon" />
            </Button>
            <p style={{
              marginTop: '16px',
              fontSize: '13px',
              color: 'var(--medium-gray)',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              *Performa historis tidak menjamin hasil di masa depan
            </p>
          </div>
        </div>
      </section>

      {/* How It Works - 3 Simple Steps */}
      <HowItWorks onCTAClick={() => {
        trackLead();
        window.open('https://t.me/saharacopytrade', '_blank');
      }} />

      {/* Trust & Safety Section */}
      <TrustSafety />

      {/* Testimonials - Social Proof with Trustpilot */}
      <section id="proof" className="testimonials-section">
        <div className="section-container">
          {/* Trustpilot Header */}
          <div className="trustpilot-badge-header">
            <Star className="trustpilot-star-icon" fill="#00B67A" stroke="#00B67A" />
            <span className="trustpilot-text-badge">Trustpilot</span>
          </div>
          <div className="trustpilot-rating-badge">
            <div className="badge-stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="badge-star" fill="#00B67A" stroke="#00B67A" />
              ))}
            </div>
            <span className="badge-score">TrustScore 4.9</span>
            <span className="badge-count">2,847 reviews</span>
          </div>
          
          <h2 className="section-title">Trader Asli. Kebebasan Nyata.</h2>
          <p className="testimonials-intro">Ribuan orang udah beralih. Ini kata mereka.</p>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="quote-mark">"</div>
                  <p className="testimonial-text">{testimonial.text}</p>
                  <div className="testimonial-author">
                    <div className="author-avatar">{testimonial.initial}</div>
                    <div>
                      <p className="author-name">{testimonial.name}</p>
                      <p className="author-location">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="comparison-section">
        <div className="section-container">
          <ComparisonTable />
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="calculator-section">
        <div className="section-container">
          <ROICalculator />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-container">
          <FAQ />
        </div>
      </section>

      {/* Final CTA Section - The Close */}
      <section className="final-cta-section-new">
        <div className="cta-background-waves"></div>
        <div className="section-container">
          <div className="final-message">
            <h2 className="final-cta-title-new">
              Siap Memulai Copy Trading?
            </h2>
            <p className="final-cta-description">
              Bergabunglah dengan komunitas trader yang menggunakan sistem copy trading berbasis AI.<br />
              Akses strategi terverifikasi, transparansi penuh, dan kontrol atas modal kamu sendiri.
            </p>
          </div>
          <div className="final-cta-buttons">
            <Button className="btn-primary-sahara btn-xl" onClick={() => {
              trackLead('Final CTA - Mulai Sekarang');
              window.open('https://t.me/saharacopytrade', '_blank');
            }} style={{
              fontSize: '18px',
              padding: '20px 40px'
            }}>
              Mulai Sekarang
              <ArrowRight className="btn-icon" />
            </Button>
            <p style={{
              fontSize: '14px',
              color: 'var(--dark-gray)',
              marginTop: '20px',
              maxWidth: '600px',
              margin: '20px auto 0'
            }}>
              Gratis untuk memulai • Tanpa biaya tersembunyi • Partner dengan broker teregulasi<br/>
              Support tersedia 24/7 untuk membantu perjalanan trading kamu
            </p>
            <p className="final-trust-badge" style={{ marginTop: '24px' }}>
              <Shield className="trust-icon" />
              Platform Teregulasi • Transparan • Aman
            </p>
            <p style={{
              fontSize: '12px',
              color: 'rgba(0, 0, 0, 0.5)',
              marginTop: '16px',
              fontStyle: 'italic',
              maxWidth: '700px',
              margin: '16px auto 0'
            }}>
              *Disclaimer: Trading dan investasi melibatkan risiko kehilangan modal. Performa masa lalu tidak menjamin hasil di masa depan. Pastikan kamu memahami risiko sebelum memulai trading.
            </p>
          </div>
        </div>
      </section>

      {/* Step by Step Registration CTA */}
      <section style={{
        background: 'linear-gradient(135deg, rgba(227, 24, 55, 0.08) 0%, rgba(255, 23, 68, 0.05) 100%)',
        borderTop: '2px solid rgba(227, 24, 55, 0.2)',
        borderBottom: '2px solid rgba(227, 24, 55, 0.2)',
        padding: window.innerWidth <= 768 ? '40px 20px' : '60px 20px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: window.innerWidth <= 768 ? '0 16px' : '0' }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '1.75rem' : window.innerWidth <= 1024 ? '2rem' : '2.5rem',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #E31837 0%, #FF1744 50%, #C4122F 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '16px',
            fontFamily: "'Space Grotesk', sans-serif",
            lineHeight: 1.2
          }}>
            Siap Mulai Perjalanan Trading Kamu?
          </h3>
          <p style={{
            fontSize: window.innerWidth <= 768 ? '1rem' : '1.125rem',
            color: 'rgba(0, 0, 0, 0.7)',
            marginBottom: '32px',
            lineHeight: 1.6
          }}>
            Ikuti proses registrasi step-by-step yang simpel dan mulai copy strategi menang hari ini
          </p>
          <button
            onClick={() => {
              trackLead();
              window.open('https://register.saharacopytrade.com/', '_blank');
            }}
            style={{
              padding: window.innerWidth <= 768 ? '16px 32px' : '20px 48px',
              fontSize: window.innerWidth <= 768 ? '1rem' : '1.25rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #E31837 0%, #FF1744 100%)',
              color: '#FFF',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 8px 24px rgba(227, 24, 55, 0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: "'Space Grotesk', sans-serif",
              width: window.innerWidth <= 768 ? '100%' : 'auto',
              maxWidth: window.innerWidth <= 768 ? '400px' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(227, 24, 55, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(227, 24, 55, 0.4)';
            }}
          >
            🚀 DAFTAR STEP BY STEP
          </button>
          <p style={{
            marginTop: '20px',
            fontSize: window.innerWidth <= 768 ? '0.8125rem' : '0.875rem',
            color: 'rgba(0, 0, 0, 0.6)',
            fontWeight: 500
          }}>
            ⚡ Setup Cepat • 📝 Proses Mudah • ✅ Langsung Mulai
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="sahara-footer">
        <div className="footer-content">
          <div className="footer-logo-container">
            <img src="/images/sahara-logo.png" alt="Sahara AI Ecosystem" className="footer-logo-img" />
          </div>
          <p className="footer-text">© 2025 Sahara AI Copytrade Ecosystem. Hak Cipta Dilindungi.</p>
          <div className="footer-links">
            <a href="#" className="footer-link">Syarat & Ketentuan</a>
            <span className="footer-divider">|</span>
            <a href="#" className="footer-link">Privasi</a>
            <span className="footer-divider">|</span>
            <a href="#" className="footer-link">Hubungi Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
